import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';

const DialogContext = createContext(null);

export const useDialog = () => {
    const ctx = useContext(DialogContext);
    if (!ctx) {
        throw new Error('useDialog must be used within DialogProvider');
    }
    return ctx;
};

export const DialogProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const [confirmState, setConfirmState] = useState(null);
    const [promptState, setPromptState] = useState(null);
    const [promptValues, setPromptValues] = useState({});
    const [promptError, setPromptError] = useState('');
    const idRef = useRef(0);

    const dismissToast = useCallback((id) => {
        setToasts((list) => list.filter((t) => t.id !== id));
    }, []);

    const toast = useMemo(() => {
        const show = (message, type = 'info') => {
            if (!message) return;
            const id = ++idRef.current;
            setToasts((list) => [...list, { id, message: String(message), type }]);
            window.setTimeout(() => dismissToast(id), 4500);
        };
        show.success = (message) => show(message, 'success');
        show.error = (message) => show(message, 'error');
        show.info = (message) => show(message, 'info');
        return show;
    }, [dismissToast]);

    const confirm = useCallback((opts = {}) => {
        const message = typeof opts === 'string' ? opts : opts.message;
        return new Promise((resolve) => {
            setConfirmState({
                title: opts.title || 'Confirm',
                message: message || '',
                confirmLabel: opts.confirmLabel || 'Confirm',
                cancelLabel: opts.cancelLabel || 'Cancel',
                danger: opts.danger !== false,
                resolve,
            });
        });
    }, []);

    const prompt = useCallback((opts = {}) => {
        const fields = opts.fields || [
            {
                key: 'value',
                label: opts.label || opts.title || 'Value',
                defaultValue: opts.defaultValue || '',
                placeholder: opts.placeholder || '',
                required: !!opts.required,
                multiline: !!opts.multiline,
                type: opts.inputType || 'text',
            },
        ];
        const initial = {};
        fields.forEach((f) => {
            initial[f.key] = f.defaultValue ?? '';
        });
        return new Promise((resolve) => {
            setPromptError('');
            setPromptValues(initial);
            setPromptState({
                title: opts.title || 'Enter value',
                confirmLabel: opts.confirmLabel || 'Save',
                cancelLabel: opts.cancelLabel || 'Cancel',
                danger: !!opts.danger,
                fields,
                single: !opts.fields,
                resolve,
            });
        });
    }, []);

    const closeConfirm = (ok) => {
        if (!confirmState) return;
        confirmState.resolve(!!ok);
        setConfirmState(null);
    };

    const closePrompt = (ok) => {
        if (!promptState) return;
        if (!ok) {
            promptState.resolve(null);
            setPromptState(null);
            setPromptError('');
            return;
        }
        const missing = promptState.fields.find((f) => f.required && !String(promptValues[f.key] || '').trim());
        if (missing) {
            setPromptError(`${missing.label} is required.`);
            return;
        }
        const result = promptState.single ? (promptValues.value ?? '') : { ...promptValues };
        promptState.resolve(result);
        setPromptState(null);
        setPromptError('');
    };

    const value = useMemo(() => ({ toast, confirm, prompt }), [toast, confirm, prompt]);

    const toastClass = {
        success: 'bg-green-600',
        error: 'bg-red-600',
        info: 'bg-gray-800',
    };

    return (
        <DialogContext.Provider value={value}>
            {children}

            <div className="fixed top-4 right-4 z-[80] flex flex-col gap-2 w-full max-w-sm pointer-events-none">
                {toasts.map((t) => (
                    <div
                        key={t.id}
                        className={`pointer-events-auto text-white text-sm rounded-lg shadow-lg px-4 py-3 flex items-start gap-2 ${toastClass[t.type] || toastClass.info}`}
                    >
                        <p className="flex-1 whitespace-pre-wrap">{t.message}</p>
                        <button type="button" className="shrink-0 opacity-80 hover:opacity-100" onClick={() => dismissToast(t.id)} aria-label="Dismiss">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                ))}
            </div>

            {confirmState && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50" onClick={() => closeConfirm(false)} />
                    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-semibold text-gray-900">{confirmState.title}</h3>
                        <p className="mt-2 text-sm text-gray-600 whitespace-pre-wrap">{confirmState.message}</p>
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                type="button"
                                className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                                onClick={() => closeConfirm(false)}
                            >
                                {confirmState.cancelLabel}
                            </button>
                            <button
                                type="button"
                                className={`px-4 py-2 text-sm rounded-md text-white ${confirmState.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                                onClick={() => closeConfirm(true)}
                            >
                                {confirmState.confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {promptState && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50" onClick={() => closePrompt(false)} />
                    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md p-6">
                        <h3 className="text-lg font-semibold text-gray-900">{promptState.title}</h3>
                        <div className="mt-4 space-y-3">
                            {promptState.fields.map((f) => (
                                <div key={f.key}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        {f.label}{f.required ? ' *' : ''}
                                    </label>
                                    {f.multiline ? (
                                        <textarea
                                            rows={4}
                                            value={promptValues[f.key] || ''}
                                            placeholder={f.placeholder}
                                            onChange={(e) => setPromptValues((v) => ({ ...v, [f.key]: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    ) : (
                                        <input
                                            type={f.type || 'text'}
                                            value={promptValues[f.key] || ''}
                                            placeholder={f.placeholder}
                                            onChange={(e) => setPromptValues((v) => ({ ...v, [f.key]: e.target.value }))}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        />
                                    )}
                                </div>
                            ))}
                            {promptError && <p className="text-sm text-red-600">{promptError}</p>}
                        </div>
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                type="button"
                                className="px-4 py-2 text-sm rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                                onClick={() => closePrompt(false)}
                            >
                                {promptState.cancelLabel}
                            </button>
                            <button
                                type="button"
                                className={`px-4 py-2 text-sm rounded-md text-white ${promptState.danger ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                                onClick={() => closePrompt(true)}
                            >
                                {promptState.confirmLabel}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DialogContext.Provider>
    );
};
